import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { DashboardLayout } from '../../layouts/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import { getActiveDeliveryRoutes, getRouteGeometryAdmin } from '../../services/api';
import { WS_URL } from '../../config';
import {
  Truck, MapPin, Package, Clock, ChevronDown, ChevronUp,
  RefreshCw, Navigation, CheckCircle2, AlertCircle, Phone
} from 'lucide-react';

// ─── Leaflet Icon Helpers ────────────────────────────────────────────

const createDriverArrowSvg = (color = '#2563EB', heading = 0) => `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" width="40" height="40" style="transform: rotate(${heading}deg);">
  <circle cx="32" cy="32" r="28" fill="${color}" opacity="0.9"/>
  <circle cx="32" cy="32" r="24" fill="${color}"/>
  <path d="M32 14 L42 38 L32 32 L22 38 Z" fill="white"/>
</svg>`;

const ROUTE_COLORS = ['#2563EB', '#DC2626', '#059669', '#D97706', '#7C3AED', '#DB2777'];

const createDriverIcon = (color, heading = 0) => L.divIcon({
  html: `<div style="width:40px;height:40px;">${createDriverArrowSvg(color, heading)}</div>`,
  className: '',
  iconSize: [40, 40],
  iconAnchor: [20, 20],
  popupAnchor: [0, -20],
});

const createNumberedIcon = (number, color = '#DC2626') => L.divIcon({
  html: `<div style="
    width:28px;height:28px;border-radius:50%;
    background:${color};color:white;
    display:flex;align-items:center;justify-content:center;
    font-weight:700;font-size:13px;
    border:2px solid white;
    box-shadow:0 2px 6px rgba(0,0,0,0.3);
  ">${number}</div>`,
  className: '',
  iconSize: [28, 28],
  iconAnchor: [14, 14],
  popupAnchor: [0, -14],
});

const branchIcon = L.divIcon({
  html: `<div style="
    width:32px;height:32px;border-radius:50%;
    background:#0F172A;color:white;
    display:flex;align-items:center;justify-content:center;
    border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.3);
  "><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg></div>`,
  className: '',
  iconSize: [32, 32],
  iconAnchor: [16, 16],
  popupAnchor: [0, -16],
});

// ─── Map auto-fit component ──────────────────────────────────────────

function MapBoundsUpdater({ routes }) {
  const map = useMap();
  useEffect(() => {
    const points = [];
    routes.forEach(r => {
      if (r.driver_location?.lat) points.push([r.driver_location.lat, r.driver_location.lng]);
      if (r.branch_location?.lat) points.push([r.branch_location.lat, r.branch_location.lng]);
      (r.orders || []).forEach(o => {
        if (o.delivery_location?.lat) points.push([o.delivery_location.lat, o.delivery_location.lng]);
      });
    });
    if (points.length > 0) {
      map.fitBounds(points, { padding: [40, 40], maxZoom: 15 });
    }
  }, [routes.length]); // only on route count change
  return null;
}

// ─── Status helpers ──────────────────────────────────────────────────

const STATUS_LABELS = {
  assigned: 'Asignado',
  picked_up: 'Recogido',
  in_transit: 'En camino',
  nearby: 'Cerca',
  delivered: 'Entregado',
  failed: 'Fallido',
  created: 'Creada',
  in_progress: 'En progreso',
};

const STATUS_COLORS = {
  assigned: 'bg-blue-100 text-blue-700',
  picked_up: 'bg-yellow-100 text-yellow-700',
  in_transit: 'bg-indigo-100 text-indigo-700',
  nearby: 'bg-orange-100 text-orange-700',
  delivered: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
  created: 'bg-gray-100 text-gray-600',
  in_progress: 'bg-teal-100 text-teal-700',
};

// ─── Main Component ──────────────────────────────────────────────────

export default function SeguimientoEntregas() {
  const { user } = useAuth();
  const businessId = user?.business_id;

  const [routes, setRoutes] = useState([]);
  const [geometries, setGeometries] = useState({}); // route_id -> polyline
  const [expandedRoute, setExpandedRoute] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);

  // Live driver positions from WS (override polling data)
  const driverPositions = useRef({}); // driver_id -> {lat, lng}
  const wsRef = useRef(null);
  const pollingRef = useRef(null);

  // ── Fetch active routes ──────────────────────────────────────────
  const fetchRoutes = useCallback(async () => {
    if (!businessId) return;
    try {
      const res = await getActiveDeliveryRoutes(businessId);
      setRoutes(res.data.routes || []);
      setLastUpdate(new Date());
    } catch (err) {
      console.error('Error fetching active routes:', err);
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  // Initial load + polling every 15s
  useEffect(() => {
    fetchRoutes();
    pollingRef.current = setInterval(fetchRoutes, 15000);
    return () => clearInterval(pollingRef.current);
  }, [fetchRoutes]);

  // ── Fetch geometry when routes change ────────────────────────────
  useEffect(() => {
    routes.forEach(async (route) => {
      if (geometries[route.id]) return; // already fetched
      const driverLoc = route.driver_location || route.branch_location;
      if (!driverLoc) return;
      try {
        const res = await getRouteGeometryAdmin(route.id, driverLoc);
        if (res.data.geometry?.length) {
          setGeometries(prev => ({ ...prev, [route.id]: res.data.geometry }));
        }
      } catch (e) {
        // geometry is optional, ignore errors
      }
    });
  }, [routes]);

  // ── WebSocket for real-time driver locations ─────────────────────
  useEffect(() => {
    if (!businessId) return;
    const token = localStorage.getItem('token');
    if (!token) return;

    const connect = () => {
      const ws = new WebSocket(`${WS_URL}/api/v1/delivery/ws/business/${businessId}?token=${token}`);

      ws.onopen = () => {
        // keep alive
        const pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send('ping');
        }, 30000);
        ws._pingInterval = pingInterval;
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === 'driver_location') {
            driverPositions.current[msg.driver_id] = msg.location;
            // Update routes state with new driver location (without full re-fetch)
            setRoutes(prev => prev.map(r =>
              r.driver_id === msg.driver_id
                ? { ...r, driver_location: msg.location }
                : r
            ));
          }
        } catch {}
      };

      ws.onclose = () => {
        clearInterval(ws._pingInterval);
        // Reconnect after 3s
        setTimeout(connect, 3000);
      };

      ws.onerror = () => ws.close();
      wsRef.current = ws;
    };

    connect();

    return () => {
      if (wsRef.current) {
        clearInterval(wsRef.current._pingInterval);
        wsRef.current.onclose = null; // prevent reconnect on cleanup
        wsRef.current.close();
      }
    };
  }, [businessId]);

  // ── Derived data for map ─────────────────────────────────────────

  const mapCenter = useMemo(() => {
    for (const r of routes) {
      if (r.driver_location?.lat) return [r.driver_location.lat, r.driver_location.lng];
      if (r.branch_location?.lat) return [r.branch_location.lat, r.branch_location.lng];
    }
    return [19.4326, -99.1332]; // Default: CDMX
  }, [routes]);

  // ── Render ───────────────────────────────────────────────────────

  const content = (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Navigation className="w-6 h-6 text-teal-600" />
            Seguimiento de Entregas
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {routes.length > 0
              ? `${routes.length} ruta${routes.length > 1 ? 's' : ''} activa${routes.length > 1 ? 's' : ''}`
              : 'Sin rutas activas'}
            {lastUpdate && (
              <span className="ml-2">
                — actualizado {lastUpdate.toLocaleTimeString()}
              </span>
            )}
          </p>
        </div>
        <button
          onClick={() => { setLoading(true); fetchRoutes(); }}
          className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 text-sm font-medium text-slate-600"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Actualizar
        </button>
      </div>

      {routes.length === 0 && !loading ? (
        <EmptyState />
      ) : (
        <div className="flex flex-col lg:flex-row gap-4" style={{ height: 'calc(100vh - 180px)' }}>
          {/* Map */}
          <div className="w-full lg:w-[65%] rounded-xl overflow-hidden border border-slate-200 shadow-sm" style={{ minHeight: 400 }}>
            <MapContainer center={mapCenter} zoom={13} style={{ height: '100%', width: '100%' }} zoomControl={true}>
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <MapBoundsUpdater routes={routes} />

              {routes.map((route, idx) => {
                const color = ROUTE_COLORS[idx % ROUTE_COLORS.length];
                const pendingOrders = (route.orders || []).filter(o => o.status !== 'delivered' && o.status !== 'failed');

                return (
                  <React.Fragment key={route.id}>
                    {/* Branch marker */}
                    {route.branch_location?.lat && (
                      <Marker
                        position={[route.branch_location.lat, route.branch_location.lng]}
                        icon={branchIcon}
                      >
                        <Popup>{route.branch_name || 'Sucursal'}</Popup>
                      </Marker>
                    )}

                    {/* Driver marker */}
                    {route.driver_location?.lat && (
                      <Marker
                        position={[route.driver_location.lat, route.driver_location.lng]}
                        icon={createDriverIcon(color)}
                      >
                        <Popup>
                          <strong>{route.driver_name}</strong>
                          <br />Ruta: {route.delivered_orders}/{route.total_orders} entregas
                        </Popup>
                      </Marker>
                    )}

                    {/* Delivery destination markers (pending only) */}
                    {pendingOrders.map((order, oi) => (
                      order.delivery_location?.lat && (
                        <Marker
                          key={order.order_id}
                          position={[order.delivery_location.lat, order.delivery_location.lng]}
                          icon={createNumberedIcon(oi + 1, color)}
                        >
                          <Popup>
                            <strong>#{order.sequence || oi + 1}</strong> — {order.customer_name || 'Cliente'}
                            <br />{order.delivery_address || ''}
                            <br /><span className="text-xs">{STATUS_LABELS[order.status] || order.status}</span>
                          </Popup>
                        </Marker>
                      )
                    ))}

                    {/* Route polyline */}
                    {geometries[route.id] && (
                      <Polyline
                        positions={geometries[route.id]}
                        pathOptions={{ color, weight: 4, opacity: 0.7, dashArray: '8 4' }}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </MapContainer>
          </div>

          {/* Routes Panel */}
          <div className="w-full lg:w-[35%] overflow-y-auto space-y-3" style={{ maxHeight: 'calc(100vh - 180px)' }}>
            {routes.map((route, idx) => (
              <RouteCard
                key={route.id}
                route={route}
                color={ROUTE_COLORS[idx % ROUTE_COLORS.length]}
                expanded={expandedRoute === route.id}
                onToggle={() => setExpandedRoute(expandedRoute === route.id ? null : route.id)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );

  return <DashboardLayout>{content}</DashboardLayout>;
}

// ─── Route Card ──────────────────────────────────────────────────────

function RouteCard({ route, color, expanded, onToggle }) {
  const progress = route.total_orders > 0
    ? Math.round((route.delivered_orders / route.total_orders) * 100)
    : 0;

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      {/* Header */}
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 p-4 hover:bg-slate-50 transition-colors text-left"
      >
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: color + '18', color }}
        >
          <Truck className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-slate-800 truncate">{route.driver_name}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[route.status] || 'bg-gray-100 text-gray-600'}`}>
              {STATUS_LABELS[route.status] || route.status}
            </span>
          </div>
          <p className="text-xs text-slate-500 mt-0.5 truncate">
            {route.branch_name} — {route.total_orders} pedido{route.total_orders !== 1 ? 's' : ''}
          </p>
          {/* Progress bar */}
          <div className="mt-2 flex items-center gap-2">
            <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{ width: `${progress}%`, backgroundColor: color }}
              />
            </div>
            <span className="text-xs font-medium text-slate-600">{progress}%</span>
          </div>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>

      {/* Expanded order list */}
      {expanded && (
        <div className="border-t border-slate-100 px-4 py-3 space-y-2">
          {route.driver_phone && (
            <a
              href={`tel:${route.driver_phone}`}
              className="flex items-center gap-2 text-sm text-teal-600 hover:text-teal-700 mb-2"
            >
              <Phone className="w-4 h-4" /> {route.driver_phone}
            </a>
          )}
          {(route.orders || []).map((order, i) => (
            <div
              key={order.order_id}
              className={`flex items-center gap-3 p-2 rounded-lg ${
                order.status === 'delivered' ? 'bg-green-50' : order.status === 'failed' ? 'bg-red-50' : 'bg-slate-50'
              }`}
            >
              <span className="w-6 h-6 rounded-full bg-white border border-slate-200 flex items-center justify-center text-xs font-bold text-slate-600">
                {order.sequence || i + 1}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-700 truncate">
                  {order.customer_name || `Pedido #${order.order_id?.slice(-6)}`}
                </p>
                <p className="text-xs text-slate-500 truncate">{order.delivery_address || ''}</p>
              </div>
              {order.status === 'delivered' ? (
                <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
              ) : order.status === 'failed' ? (
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
              ) : (
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${STATUS_COLORS[order.status] || 'bg-gray-100 text-gray-600'}`}>
                  {STATUS_LABELS[order.status] || order.status}
                </span>
              )}
            </div>
          ))}
          {route.orders?.length === 0 && (
            <p className="text-sm text-slate-400 text-center py-2">Sin pedidos en esta ruta</p>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Empty State ─────────────────────────────────────────────────────

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mb-4">
        <MapPin className="w-10 h-10 text-slate-400" />
      </div>
      <h3 className="text-lg font-semibold text-slate-700 mb-1">Sin entregas activas</h3>
      <p className="text-slate-500 text-sm max-w-sm">
        Cuando tus repartidores inicien una ruta de entrega, podrás ver su progreso aquí en tiempo real.
      </p>
    </div>
  );
}
